/**
 * Player Journey Funnel
 * Visualize the complete player lifecycle from discovery to champion
 * Solis Games - Phase 2.2 Implementation
 */

import React from 'react';
import Icon from '../../../components/AppIcon';

const PlayerJourneyFunnel = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading player journey data...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.funnel || data.funnel.length === 0) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Journey Data Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Player journey tracking will begin once players start discovering and playing your game.
            Make sure journey events are being tracked in your game integration.
          </p>
        </div>
      </div>
    );
  }

  const getStageIcon = (stage) => {
    const icons = {
      discovered: 'Eye',
      interested: 'MousePointer',
      first_play: 'Play',
      return_player: 'RotateCcw',
      engaged: 'Heart',
      champion: 'Award'
    };
    return icons[stage] || 'Circle';
  };

  const getStageColor = (stage) => {
    const colors = {
      discovered: 'bg-blue-500',
      interested: 'bg-cyan-500',
      first_play: 'bg-green-500',
      return_player: 'bg-yellow-500',
      engaged: 'bg-orange-500',
      champion: 'bg-purple-500'
    };
    return colors[stage] || 'bg-gray-500';
  };

  const getConversionColor = (rate) => {
    if (rate >= 70) return 'text-green-500';
    if (rate >= 50) return 'text-yellow-500';
    if (rate >= 30) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div>
          <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="TrendingUp" size={24} className="text-primary" />
            Player Journey Funnel
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Track the complete player lifecycle from discovery to championship
          </p>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="p-8">
        <div className="space-y-4">
          {data.funnel.map((stage, idx) => {
            const isFirst = idx === 0;
            const prevStage = isFirst ? null : data.funnel[idx - 1];
            const conversionRate = stage.conversion_rate || 100;
            const dropOffRate = prevStage ? (100 - conversionRate).toFixed(1) : 0;
            const isCriticalDropOff = dropOffRate > 70;
            const isHighDropOff = dropOffRate > 50 && dropOffRate <= 70;

            // Calculate funnel width percentage
            const maxUsers = data.funnel[0]?.users || 1;
            const widthPercent = Math.max(10, (stage.users / maxUsers) * 100);

            return (
              <div key={stage.stage} className="relative">
                {/* Stage Card */}
                <div
                  className="relative mx-auto transition-all hover:scale-[1.02]"
                  style={{ width: `${widthPercent}%` }}
                >
                  <div className={`bg-gradient-to-r ${getStageColor(stage.stage)}/10 border-2 ${getStageColor(stage.stage).replace('bg-', 'border-')} rounded-lg p-4`}>
                    <div className="flex items-center justify-between">
                      {/* Left: Icon & Stage Name */}
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getStageColor(stage.stage)} shadow-lg`}>
                          <Icon name={getStageIcon(stage.stage)} size={24} className="text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground capitalize text-lg">
                            {stage.stage.replace('_', ' ')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stage.stage === 'discovered' && 'Viewed game page'}
                            {stage.stage === 'interested' && 'Clicked play or favorited'}
                            {stage.stage === 'first_play' && 'Started first session'}
                            {stage.stage === 'return_player' && 'Came back after 24h'}
                            {stage.stage === 'engaged' && '5+ sessions or 60+ minutes'}
                            {stage.stage === 'champion' && 'Favorited + 10+ sessions'}
                          </div>
                        </div>
                      </div>

                      {/* Right: Metrics */}
                      <div className="text-right">
                        <div className="text-3xl font-bold text-foreground">
                          {stage.users.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">players</div>
                      </div>
                    </div>

                    {/* Conversion Rate Badge */}
                    {!isFirst && (
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon name="ArrowDownRight" size={16} className="text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Conversion from previous stage</span>
                          </div>
                          <div className={`text-lg font-bold ${getConversionColor(conversionRate)}`}>
                            {conversionRate.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Drop-off Warning */}
                  {(isCriticalDropOff || isHighDropOff) && (
                    <div className={`mt-2 p-3 rounded-lg ${isCriticalDropOff ? 'bg-red-500/10 border border-red-500/20' : 'bg-orange-500/10 border border-orange-500/20'}`}>
                      <div className="flex items-start gap-2">
                        <Icon name="AlertTriangle" size={16} className={isCriticalDropOff ? 'text-red-500' : 'text-orange-500'} />
                        <div className="flex-1">
                          <div className={`text-xs font-semibold ${isCriticalDropOff ? 'text-red-500' : 'text-orange-500'}`}>
                            {isCriticalDropOff ? 'Critical' : 'High'} Drop-Off: {dropOffRate}% lost
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {prevStage && stage.users && `${(prevStage.users - stage.users).toLocaleString()} players dropped off at this stage`}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Connecting Arrow */}
                {!isFirst && (
                  <div className="flex justify-center my-2">
                    <Icon name="ChevronDown" size={32} className="text-muted-foreground" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drop-off Analysis */}
      {data.dropOffAnalysis && data.dropOffAnalysis.length > 0 && (
        <div className="p-6 border-t border-border bg-red-500/5">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="AlertTriangle" size={18} className="text-red-500" />
            Critical Drop-Off Points
          </h4>
          <div className="space-y-3">
            {data.dropOffAnalysis.map((dropOff, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${dropOff.severity === 'critical' ? 'bg-red-500/10 border-red-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${dropOff.severity === 'critical' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'}`}>
                        {dropOff.severity.toUpperCase()}
                      </span>
                      <span className="font-semibold text-foreground text-sm">{dropOff.stage}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {dropOff.usersLost.toLocaleString()} players lost • {dropOff.dropRate}% drop-off rate
                    </p>
                    {dropOff.recommendation && (
                      <div className="mt-2 p-2 bg-card rounded border border-border">
                        <div className="flex items-start gap-2">
                          <Icon name="Lightbulb" size={14} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-foreground">{dropOff.recommendation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Discovery Source Breakdown */}
      {data.sourceBreakdown && Object.keys(data.sourceBreakdown).length > 0 && (
        <div className="p-6 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Search" size={18} />
            Discovery Source Performance
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(data.sourceBreakdown.discovered || {}).map(([source, count]) => {
              const totalDiscovered = Object.values(data.sourceBreakdown.discovered || {}).reduce((a, b) => a + b, 0);
              const percentage = ((count / totalDiscovered) * 100).toFixed(1);

              return (
                <div key={source} className="bg-muted/30 border border-border rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-1 capitalize">{source}</div>
                  <div className="text-2xl font-bold text-foreground">{count.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground mt-1">{percentage}% of discoveries</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Journey Insights */}
      <div className="p-6 border-t border-border bg-muted/5">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={18} className="text-yellow-500" />
          Journey Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.funnel.length > 0 && (
            <>
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <Icon name="Eye" size={18} className="text-blue-500 mb-2" />
                <div className="text-sm font-medium text-foreground">Discovery Rate</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.funnel[0]?.users.toLocaleString()} players discovered your game
                </div>
              </div>

              {data.funnel.find(s => s.stage === 'return_player') && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Icon name="RotateCcw" size={18} className="text-green-500 mb-2" />
                  <div className="text-sm font-medium text-foreground">Retention Success</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {((data.funnel.find(s => s.stage === 'return_player')?.users / data.funnel[0]?.users) * 100).toFixed(1)}% of players return after first session
                  </div>
                </div>
              )}

              {data.funnel.find(s => s.stage === 'champion') && (
                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <Icon name="Award" size={18} className="text-purple-500 mb-2" />
                  <div className="text-sm font-medium text-foreground">Champion Conversion</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {((data.funnel.find(s => s.stage === 'champion')?.users / data.funnel[0]?.users) * 100).toFixed(1)}% become champions (super fans)
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerJourneyFunnel;
